/* eslint-disable max-lines-per-function */
import { FieldBase } from '@/components/form';
import { InfoPopover } from '@/components/ui/info';
import { ZuschlagsgruppeSelect } from '@/domain/mitarbeiter/features/anlageassistent/content/Bezahlung/Zuschlaege/ZuschlagsgruppeSelect';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { useAnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/useAnlageassistentContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Skeleton,
  Toggle,
} from '@e2n/ui';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

export function Zuschlaege() {
  const { ctx } = useAnlageassistentContext();
  const { t } = useTranslation();
  const form = useAnlageassistentFormContext();
  return (
    <form.Subscribe
      selector={(state) => ({
        zuschlaege: state.values.bezahlung.zuschlaege,
      })}>
      {({ zuschlaege }) => (
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            <div className="flex gap-1 items-center">
              <h2 className="text-neutrals-900 text-xl font-bold leading-7">
                {ctx.getCurrentSubStep()?.title}
              </h2>
              <InfoPopover
                text={t(
                  'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.info',
                )}
              />
            </div>
            <form.AppField
              name="bezahlung.zuschlaege.zuschlaege"
              listeners={{
                onChange: () => {
                  ctx.resetDependentFields('bezahlung.zuschlaege.zuschlaege');
                },
              }}>
              {(field) => (
                <FieldBase>
                  <div className="col-span-2 grid md:grid-cols-2 items-start gap-4">
                    <Toggle
                      pressed={field.state.value === true}
                      onPressedChange={() => field.setValue(true)}
                      variant={'outline'}
                      className="flex items-center justify-start gap-2 p-4 h-auto">
                      <span className="text-neutrals-900 text-sm font-semibold leading-5">
                        {t(
                          'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.zuschlaege.options.true',
                        )}
                      </span>
                    </Toggle>
                    <Toggle
                      pressed={field.state.value === false}
                      onPressedChange={() => field.setValue(false)}
                      variant={'outline'}
                      className="flex items-center justify-start gap-2 p-4 h-auto">
                      <span className="text-neutrals-900 text-sm font-semibold leading-5">
                        {t(
                          'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.zuschlaege.options.false',
                        )}
                      </span>
                    </Toggle>
                  </div>
                </FieldBase>
              )}
            </form.AppField>
          </div>

          {zuschlaege.zuschlaege && (
            <>
              <div className="flex flex-col gap-4">
                <span className="text-neutrals-900 text-base font-bold leading-6">
                  {t(
                    'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.zuschlagsgruppe.description',
                  )}
                </span>
                <Suspense fallback={<Skeleton className="w-96 h-10" />}>
                  <ZuschlagsgruppeSelect />
                </Suspense>
              </div>
              <div className="flex flex-col gap-4">
                <Accordion type="single" collapsible>
                  <AccordionItem value="options">
                    <AccordionTrigger className="text-neutrals-900 text-base font-bold leading-6">
                      {t(
                        'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.optionen',
                      )}
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-4 items-start">
                        <form.AppField
                          name="bezahlung.zuschlaege.pauschaleZuschlaege"
                          listeners={{
                            onChange: () => {
                              ctx.validateCurrentSubStep();
                            },
                          }}>
                          {(field) => (
                            <field.MoneyInput
                              label={t(
                                'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.pauschaleZuschlaege.label',
                              )}
                            />
                          )}
                        </form.AppField>
                        <form.AppField
                          name="bezahlung.zuschlaege.uebertariflicheZahlung"
                          listeners={{
                            onChange: () => {
                              ctx.validateCurrentSubStep();
                            },
                          }}>
                          {(field) => (
                            <field.MoneyInput
                              label={t(
                                'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.uebertariflicheZahlung.label',
                              )}
                            />
                          )}
                        </form.AppField>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <form.Subscribe
                  selector={(state) =>
                    state.fieldMeta['bezahlung.zuschlaege']?.errorMap?.onSubmit
                  }>
                  {(error) =>
                    error ? (
                      <Alert
                        variant="destructive"
                        className="bg-primary-red-100 border-primary-red-400">
                        <FontAwesomeIcon icon={faCircleExclamation} />
                        <AlertTitle className="text-neutrals-900 text-sm font-semibold leading-5">
                          {t(
                            'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.validationErrorAlert.title',
                          )}
                        </AlertTitle>
                        <AlertDescription className="text-neutrals-800 text-xs font-normal leading-4">
                          {t(
                            'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.validationErrorAlert.description',
                          )}
                        </AlertDescription>
                      </Alert>
                    ) : null
                  }
                </form.Subscribe>
              </div>
            </>
          )}
        </div>
      )}
    </form.Subscribe>
  );
}
